import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { BuildingOffice2Icon, PrinterIcon } from "@heroicons/react/24/outline";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { PMPagination } from "../../../components/utils/PMPagination";
import { LabelDesignerDialog } from "../../../components/labels/LabelDesignerDialog";
import { loadProductRecords } from "../../../components/labels/labelRecords";
import { getStorages } from "../../../slices/inventory/storage";
import { warehouseStockApi } from "../../../api/inventory/warehouse-stock";
import { formatDecimal } from "./inventory-format";

const PAGE_SIZE = 50;
const card = "overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.07)]";

function readFilters(params) {
  let storages = [];
  try {
    const value = JSON.parse(params.get("storages") || "[]");
    if (Array.isArray(value)) storages = value.map(String);
  } catch (_) { /* Ignore malformed URLs. */ }
  return {
    query: params.get("query") || "",
    storages,
    tracked_quantity_min: params.get("min_tracked_quantity") || "",
    tracked_quantity_max: params.get("max_tracked_quantity") || "",
  };
}

function productName(item) {
  return item.product?.displayTitle || item.product?.title || item.product?.item_number || `#${item.product_id}`;
}

export const WarehouseStock = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  const storages = useSelector((state) => state.inventory_storages?.data || []);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => readFilters(searchParams));
  const [page, setPage] = useState(() => Math.max(1, Number.parseInt(searchParams.get("page"), 10) || 1));
  const [perPage, setPerPage] = useState(() => Math.max(1, Number.parseInt(searchParams.get("per_page"), 10) || PAGE_SIZE));
  const [result, setResult] = useState({ companyId: null, rows: [], total: 0, stockSource: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revision, setRevision] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [labelPrintDialogOpen, setLabelPrintDialogOpen] = useState(false);
  const [storagesCompanyId, setStoragesCompanyId] = useState(null);
  const sequence = useRef(0);
  const previousCompanyId = useRef(companyId);
  const filterKey = JSON.stringify([filters.query, filters.storages, filters.tracked_quantity_min, filters.tracked_quantity_max]);
  const visibleRows = result.companyId === companyId ? result.rows : [];
  const total = result.companyId === companyId ? result.total : 0;

  useEffect(() => {
    let active = true;
    setStoragesCompanyId(null);
    Promise.resolve(dispatch(getStorages())).finally(() => { if (active) setStoragesCompanyId(companyId); });
    return () => { active = false; };
  }, [dispatch, companyId]);
  useEffect(() => {
    if (previousCompanyId.current === companyId) return;
    previousCompanyId.current = companyId;
    setFilters((current) => ({ ...current, storages: [] }));
    setPage(1);
    const params = new URLSearchParams(searchParams);
    params.delete("storages"); params.delete("page");
    setSearchParams(params, { replace: true });
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSelectedProducts(new Set());
    setLabelPrintDialogOpen(false);
  }, [companyId, filterKey, page, perPage]);

  useEffect(() => {
    const request = ++sequence.current;
    setLoading(true); setError(null);
    const timer = setTimeout(async () => {
      try {
        const response = await warehouseStockApi.getStock({
          query: filters.query,
          storage_ids: filters.storages,
          min_tracked_quantity: filters.tracked_quantity_min || undefined,
          max_tracked_quantity: filters.tracked_quantity_max || undefined,
          page, per_page: perPage,
        });
        if (request !== sequence.current) return;
        if (response?.success !== true || !Array.isArray(response.data)) throw new Error("Invalid stock response");
        setResult({
          companyId, rows: response.data,
          total: response.pagination?.total ?? response.data.length,
          stockSource: response.stock_source || null,
        });
      } catch (_) {
        if (request === sequence.current) setError("inventory.stock.loadFailed");
      } finally {
        if (request === sequence.current) setLoading(false);
      }
    }, filters.query ? 250 : 0);
    return () => { clearTimeout(timer); sequence.current += 1; };
  }, [companyId, filterKey, page, perPage, revision]); // eslint-disable-line react-hooks/exhaustive-deps

  const storageOptions = useMemo(() => (storagesCompanyId === companyId ? storages : []).map((storage) => ({
    value: String(storage.id), label: storage.title || storage.storage_number || `#${storage.id}`,
  })), [storages, storagesCompanyId, companyId]);

  const changeFilters = (next) => {
    const normalized = {
      query: (next.query || "").trim(),
      storages: (next.storages || []).map(String),
      tracked_quantity_min: next.tracked_quantity_min || "",
      tracked_quantity_max: next.tracked_quantity_max || "",
    };
    if (JSON.stringify(normalized) === JSON.stringify(filters)) return;
    setFilters(normalized); setPage(1);
    const params = new URLSearchParams(searchParams);
    ["query", "storages", "min_tracked_quantity", "max_tracked_quantity", "page"].forEach((key) => params.delete(key));
    if (normalized.query) params.set("query", normalized.query);
    if (normalized.storages.length) params.set("storages", JSON.stringify(normalized.storages));
    if (normalized.tracked_quantity_min) params.set("min_tracked_quantity", normalized.tracked_quantity_min);
    if (normalized.tracked_quantity_max) params.set("max_tracked_quantity", normalized.tracked_quantity_max);
    setSearchParams(params, { replace: true });
  };

  const changePage = (nextPage) => {
    setPage(nextPage);
    const params = new URLSearchParams(searchParams);
    if (nextPage === 1) params.delete("page"); else params.set("page", String(nextPage));
    setSearchParams(params, { replace: true });
  };

  const changePerPage = (nextPerPage) => {
    setPerPage(nextPerPage); setPage(1);
    const params = new URLSearchParams(searchParams);
    params.delete("page");
    if (nextPerPage === PAGE_SIZE) params.delete("per_page"); else params.set("per_page", String(nextPerPage));
    setSearchParams(params, { replace: true });
  };

  const toggleProduct = (id) => setSelectedProducts((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = visibleRows.length > 0 && visibleRows.every((row) => selectedProducts.has(row.product_id));
  const selectedRows = visibleRows.filter((row) => selectedProducts.has(row.product_id));
  const displayNumber = (value) => formatDecimal(value, 2) ?? "—";

  return <>
    <DashboardHeader variant="list" title={t("inventory.stock.title")} listCount={result.companyId === companyId ? total : null} />
    <main className="min-h-full bg-slate-50 p-4 pb-24 md:p-6 xl:p-8"><div className="mx-auto max-w-[1440px] space-y-5">
      <p className="text-sm text-slate-600">{t("inventory.stock.subtitle")}</p>
      <ModuleFilter filterCard onFiltersChange={changeFilters} initialFilters={filters}
        searchPlaceholder="inventory.stock.search" customDropdownFilters={[{
          key: "storages", label: t("inventory.stock.storage"), icon: BuildingOffice2Icon, options: storageOptions,
        }]} customRangeFilters={[{
          key: "tracked_quantity", label: t("inventory.stock.tracked"), icon: BuildingOffice2Icon,
          minPlaceholder: t("inventory.stock.min"), maxPlaceholder: t("inventory.stock.max"),
        }]} />
      {result.companyId === companyId && result.stockSource === "legacy" && <p role="status" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{t("inventory.stock.legacyWarning")}</p>}
      {selectedRows.length > 0 && <div role="toolbar" aria-label={t("inventory.stock.selection")} className="sticky bottom-4 z-20 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 shadow-lg"><span className="text-sm font-semibold text-[#0B3A78]">{t("inventory.stock.selected", { count: selectedRows.length })}</span><button type="button" onClick={() => setLabelPrintDialogOpen(true)} className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-[#1463C7] px-4 text-sm font-semibold text-white hover:bg-[#0B3A78]"><PrinterIcon className="h-5 w-5" aria-hidden="true" />{t("inventory.stock.printLabels")}</button></div>}
      <section className={card} aria-label={t("inventory.stock.title")}>
        {error ? <div className="p-6"><ResourceError errors={[t(error)]} onRetry={() => setRevision((old) => old + 1)} /></div>
          : loading || result.companyId !== companyId ? <div className="p-10"><ResourceLoading label={t("inventory.stock.loading")} /></div>
            : visibleRows.length === 0 ? <div className="px-5 py-12 text-center"><BuildingOffice2Icon className="mx-auto h-12 w-12 text-slate-400" aria-hidden="true" /><h2 className="mt-3 text-lg font-bold text-slate-900">{t("inventory.stock.empty")}</h2><p className="mt-1 text-sm text-slate-500">{t("inventory.stock.emptyHint")}</p>{(filters.query || filters.storages.length || filters.tracked_quantity_min || filters.tracked_quantity_max) && <button type="button" onClick={() => changeFilters({})} className="mt-4 min-h-[44px] rounded-xl border border-slate-200 px-5 text-sm font-semibold text-[#0B3A78]">{t("inventory.stock.clearFilters")}</button>}</div>
              : <><div className="overflow-x-auto"><table className="w-full min-w-[890px] text-left text-sm"><thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600"><tr><th scope="col" className="w-12 px-4 py-3"><input type="checkbox" aria-label={t("inventory.stock.selectAll")} checked={allSelected} onChange={(event) => setSelectedProducts(event.target.checked ? new Set(visibleRows.map((row) => row.product_id)) : new Set())} className="h-5 w-5 accent-[#1463C7]" /></th>{["product", "storage", "tracked", "outgoing", "incoming", "available"].map((key) => <th scope="col" key={key} className={`px-4 py-3 ${["tracked", "outgoing", "incoming", "available"].includes(key) ? "text-right" : ""}`}>{t(`inventory.stock.${key}`)}</th>)}</tr></thead><tbody className="divide-y divide-slate-100">{visibleRows.map((row) => <tr key={row.product_id} className="hover:bg-slate-50"><td className="px-4 py-3"><input type="checkbox" aria-label={t("inventory.stock.selectProduct", { product: productName(row) })} checked={selectedProducts.has(row.product_id)} onChange={() => toggleProduct(row.product_id)} className="h-5 w-5 accent-[#1463C7]" /></td><td className="px-4 py-3"><Link to={`/dashboard/products/${row.product_id}`} className="font-semibold text-[#1463C7] hover:underline">{productName(row)}</Link>{row.product?.item_number && <p className="text-xs tabular-nums text-slate-500">{row.product.item_number}</p>}</td><td className="max-w-[220px] px-4 py-3 text-slate-600">{(row.storages || []).map((storage) => storage.title || storage.storage_number || `#${storage.id}`).join(", ") || "—"}</td><td className="px-4 py-3 text-right tabular-nums text-slate-700">{displayNumber(row.tracked_quantity)}</td><td className="px-4 py-3 text-right tabular-nums text-slate-700">{displayNumber(row.outgoing_quantity)}</td><td className="px-4 py-3 text-right tabular-nums text-slate-700">{displayNumber(row.incoming_quantity)}</td><td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900">{displayNumber(row.available_quantity)}</td></tr>)}</tbody></table></div><div className="border-t border-slate-100 px-5 py-3"><PMPagination page={page} perPage={perPage} totalCount={total} currentCount={visibleRows.length} onPageChange={changePage} onPerPageChange={changePerPage} /></div></>}
      </section>
    </div></main>
    <LabelDesignerDialog module="product" open={labelPrintDialogOpen && result.companyId === companyId} onClose={() => { setLabelPrintDialogOpen(false); setSelectedProducts(new Set()); }} loadRecords={() => loadProductRecords(selectedRows)} />
  </>;
};

export default WarehouseStock;
